// SegTrack: the segmented control. A recessed well holding compact keys and one gold-tinted
// thumb that springs to the chosen one; `multi` lets several be on at once, each tinted where it
// sits. Tabs (./Tabs) is the same well with tab semantics, built on the pieces exported here.
// docs/operations/design-standard.md, "Controls" (wells and keys, segmented).

import React, { useLayoutEffect, useRef, useState } from 'react';
import Appear from './Appear';
import Tooltip from './Tooltip';
import { CONTROL_MOTION, FOCUS, PRESS, STATE, TOUCH, useTicker } from './motion';

export type SegSize = 'sm' | 'md' | 'lg';

/** What one key in a track can show. Shared by SegTrack and Tabs. */
export interface SegKeyContent {
  /** Plain text, or JSX when the caller styles part of it. Give `aria` when JSX would read badly aloud. */
  label: React.ReactNode;
  /** A second line under the label, such as a duration or a date. */
  sub?: string;
  /** Spoken name, when the label alone is ambiguous out of context. Replaces the whole name. */
  aria?: string;
  /** Drawn before the label in the key's own colour: a view's glyph, inline SVG. */
  icon?: React.ReactNode;
  /** A quiet figure beside the label: how many rows the key holds. Shown at zero. */
  count?: number | string;
  /** A filled figure asking for attention: approvals waiting, sends that failed. Hidden at zero. */
  badge?: number;
  /** `alert` paints the badge red, for a count that is a failure waiting on a person. */
  badgeTone?: 'alert';
  /** Unavailable right now. Stays focusable, so its hint can say why. */
  disabled?: boolean;
  /** Tooltip: what the key shows, or why it is unavailable. */
  hint?: string;
}

export interface SegOption extends SegKeyContent {
  key: string;
}

interface SegTrackBase {
  label: string;
  options: SegOption[];
  size?: SegSize;
  /** Equal-width keys filling the row, instead of each key sized to its label. */
  fill?: boolean;
  /** Keys flow onto a second row when the track runs out of width, instead of truncating. */
  wrap?: boolean;
  className?: string;
}

interface SegTrackSingle extends SegTrackBase {
  multi?: false;
  value: string | null;
  onChange: (key: string) => void;
  /** When given, pressing the chosen key again unsets it. */
  onClear?: () => void;
}

interface SegTrackMulti extends SegTrackBase {
  /** Any number on at once. Pressing a key toggles it: `onChange` receives the key either way. */
  multi: true;
  value: ReadonlySet<string> | readonly string[];
  onChange: (key: string) => void;
}

export type SegTrackProps = SegTrackSingle | SegTrackMulti;

const KEY_SIZE: Record<SegSize, string> = {
  sm: 'min-h-6 px-2 text-xs',
  md: 'min-h-7 px-2.5 text-[13px]',
  lg: 'min-h-9 px-3 text-sm',
};

/** The well. 2px padding and gaps, the same geometry as every other track. */
export const segTrackClass = (fill: boolean, wrap: boolean): string =>
  `relative gap-0.5 rounded-control bg-surface-container-high p-0.5 shadow-recess ${
    fill ? 'grid w-full' : `inline-flex max-w-full ${wrap ? 'flex-wrap' : ''}`
  }`;

export const segFillStyle = (fill: boolean, count: number): React.CSSProperties | undefined =>
  fill ? { gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` } : undefined;

/**
 * One key. Inactive is transparent and recedes; chosen takes gold text over the thumb, or its own
 * tint when `tinted` (multi, where there is no thumb). `pressable` keeps the state layer on a
 * chosen key that still does something when pressed.
 */
export const segKeyClass = (
  size: SegSize,
  on: boolean,
  opts: { disabled?: boolean; tinted?: boolean; pressable?: boolean } = {},
): string => {
  const state = opts.disabled
    ? 'cursor-not-allowed text-on-surface-disabled'
    : on
      ? `text-primary-ink ${opts.tinted ? 'bg-primary/15' : ''} ${opts.pressable ? `${PRESS} ${STATE}` : ''}`
      : `text-on-surface-variant hover:text-on-surface ${PRESS} ${STATE}`;
  return `z-[1] flex min-w-0 flex-col items-center justify-center rounded-inner py-0.5 font-medium leading-tight ${CONTROL_MOTION} ${TOUCH} ${FOCUS} ${KEY_SIZE[size]} ${state}`;
};

const Count: React.FC<{ value: number | string }> = ({ value }) => {
  const ticked = useTicker(typeof value === 'number' ? value : 0);
  return <span className="flex-none text-[11px] tabular-nums">{typeof value === 'number' ? ticked : value}</span>;
};

const Badge: React.FC<{ value?: number; tone?: 'alert' }> = ({ value, tone }) => (
  <Appear
    show={!!value}
    className={`inline-flex h-4 min-w-4 flex-none items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none tabular-nums ${
      tone === 'alert' ? 'bg-danger text-white' : 'bg-primary text-on-primary'
    }`}
  >
    {value}
  </Appear>
);

/**
 * What a key draws: an optional lead (a ring), icon, label, count and badge on one line, the sub
 * line under it. `stack` puts the lead above the label below `sm`, so five equal keys with rings
 * still fit a phone without truncating their names.
 */
export const SegKeyBody: React.FC<{ o: SegKeyContent; lead?: React.ReactNode; stack?: boolean }> = ({ o, lead, stack }) => (
  <>
    <span className={`flex max-w-full items-center ${stack ? 'flex-col gap-0.5 sm:flex-row sm:gap-1.5' : 'gap-1.5'}`}>
      {lead}
      {o.icon && <span aria-hidden="true" className="inline-flex flex-none">{o.icon}</span>}
      <span className="min-w-0 max-w-full truncate">{o.label}</span>
      {o.count !== undefined && <Count value={o.count} />}
      <Badge value={o.badge} tone={o.badgeTone} />
    </span>
    {o.sub && <span className="max-w-full truncate text-[11px] tabular-nums">{o.sub}</span>}
  </>
);

interface Box { left: number; top: number; width: number; height: number }

/** Layout position inside `root`. Offsets, not client rects: a dialog scaling in must not skew the thumb. */
const boxIn = (el: HTMLElement, root: HTMLElement): Box => {
  let left = 0;
  let top = 0;
  let n: HTMLElement | null = el;
  while (n && n !== root) {
    left += n.offsetLeft;
    top += n.offsetTop;
    n = n.offsetParent as HTMLElement | null;
  }
  return { left, top, width: el.offsetWidth, height: el.offsetHeight };
};

export interface SegThumbState { box: Box | null; shown: boolean; armed: boolean; glide: boolean }

/**
 * Measures the key at `active` and keeps the thumb on it through resizes, wrapping and badges
 * arriving. The thumb lands in place on first paint, fades in where it lands when nothing was
 * chosen before, fades out in place when the choice is cleared, and springs between keys.
 */
export function useSegThumb(active: number, count: number) {
  const track = useRef<HTMLDivElement>(null);
  const keys = useRef<Array<HTMLButtonElement | null>>([]);
  const [box, setBox] = useState<Box | null>(null);
  const [shown, setShown] = useState(false);
  const [armed, setArmed] = useState(false);
  const [glide, setGlide] = useState(false);
  const wasShown = useRef(false);

  useLayoutEffect(() => {
    const root = track.current;
    if (!root) return undefined;
    setGlide(wasShown.current);
    const measure = () => {
      const el = keys.current[active];
      if (!el) {
        wasShown.current = false;
        setShown(false);
        return;
      }
      const next = boxIn(el, root);
      setBox((prev) => (prev && prev.left === next.left && prev.top === next.top && prev.width === next.width && prev.height === next.height ? prev : next));
      wasShown.current = true;
      setShown(true);
    };
    measure();
    const arm = requestAnimationFrame(() => setArmed(true));
    let frame = 0;
    // Re-measure a frame after a resize, never inside the observation pass. The keys are watched
    // as well as the track: a badge arriving resizes a key while the track keeps its width.
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); })
      : null;
    if (ro) {
      ro.observe(root);
      keys.current.slice(0, count).forEach((k) => { if (k) ro.observe(k); });
    }
    return () => { cancelAnimationFrame(arm); cancelAnimationFrame(frame); ro?.disconnect(); };
  }, [active, count]);

  return { track, keys, thumb: { box, shown, armed, glide } as SegThumbState };
}

export const SegThumb: React.FC<SegThumbState> = ({ box, shown, armed, glide }) => (
  <span
    aria-hidden="true"
    className={`pointer-events-none absolute left-0 top-0 rounded-inner bg-primary/15 ${
      !armed
        ? ''
        : glide
          ? 'transition-[transform,width,height,opacity] duration-spring ease-spring motion-reduce:transition-[opacity] motion-reduce:duration-quick'
          : 'transition-[opacity] duration-standard ease-emphasized'
    }`}
    style={{
      transform: `translate(${box ? box.left : 0}px, ${box ? box.top : 0}px)`,
      width: box ? box.width : 0,
      height: box ? box.height : 0,
      opacity: shown && box ? 1 : 0,
    }}
  />
);

export const SegTrack: React.FC<SegTrackProps> = (props) => {
  const { label, options, size = 'md', fill = false, wrap = false, className = '' } = props;
  const onSet = props.multi ? new Set<string>(props.value) : null;
  const active = props.multi ? -1 : options.findIndex((o) => o.key === props.value);
  const { track, keys, thumb } = useSegThumb(active, options.length);

  return (
    <div
      ref={track}
      role="group"
      aria-label={label}
      className={`${segTrackClass(fill, wrap)} ${className}`}
      style={segFillStyle(fill, options.length)}
    >
      {!props.multi && <SegThumb {...thumb} />}
      {options.map((o, i) => {
        const on = onSet ? onSet.has(o.key) : props.value === o.key;
        const press = () => {
          if (o.disabled) return;
          if (props.multi) props.onChange(o.key);
          else if (!on) props.onChange(o.key);
          else (props as SegTrackSingle).onClear?.();
        };
        const btn = (
          <button
            key={o.key}
            ref={(b) => { keys.current[i] = b; }}
            type="button"
            aria-pressed={on}
            aria-disabled={o.disabled || undefined}
            aria-label={o.aria}
            onClick={press}
            className={segKeyClass(size, on, { disabled: o.disabled, tinted: !!props.multi, pressable: props.multi ? true : !!(props as SegTrackSingle).onClear })}
          >
            <SegKeyBody o={o} />
          </button>
        );
        return o.hint ? <Tooltip key={o.key} label={o.hint}>{btn}</Tooltip> : btn;
      })}
    </div>
  );
};

export default SegTrack;
